import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import styled from 'styled-components';
import TooltipWrapper from '../../shared/components/TooltipWrapper';

const StyledTable = styled.table`
  width: 100%;
  text-align: left;
  border-collapse: collapse;
  color: #454545;

  th,
  td {
    padding: 1em 0.2em;
    font-weight: 400;
    vertical-align: middle;
    word-break: keep-all;
    font-size: 1.4em;
  }
  th {
    padding: 0.5em 0.2em;
    word-break: keep-all;
  }
`;
const TableHeader = styled.tr`
  color: #756ba4;
`;
const GroupHeader = styled.tr`
  border-top: 1px solid ${({ theme }) => theme.borderColor};

  &:first-child {
    border-color: #675e97;
  }
`;

const isSpanningCell = cell =>
  cell !== null &&
  typeof cell === 'object' &&
  !Array.isArray(cell) &&
  !React.isValidElement(cell) &&
  cell.rowSpan !== undefined;

/**
 * A cell may be given as { content, rowSpan }; rows covered by such a span
 * list those columns in `spanned` so the cell is not rendered again.
 */
const renderCells = (row, columns) =>
  columns.map((column, index) => {
    if (row.spanned && row.spanned.includes(column.id)) {
      return null;
    }

    const cell = row[column.id];

    return isSpanningCell(cell) ? (
      <td key={index} rowSpan={cell.rowSpan}>
        {cell.content}
      </td>
    ) : (
      <td key={index}>{cell}</td>
    );
  });

const Table = props => {
  const { columns, rows } = props;

  return (
    <StyledTable>
      <thead>
        <TableHeader>
          {columns.map((column, index) => (
            <th key={index}>
              <TooltipWrapper tooltip={column.tooltip}>
                {column.label}
              </TooltipWrapper>
            </th>
          ))}
        </TableHeader>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <Fragment key={`row-${index}`}>
            <GroupHeader key={row.id}>{renderCells(row, columns)}</GroupHeader>
            {row.subrows &&
              row.subrows.map((subrow, index) => (
                <tr key={`subrow-${index}`}>{renderCells(subrow, columns)}</tr>
              ))}
          </Fragment>
        ))}
      </tbody>
    </StyledTable>
  );
};

Table.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string
    })
  ).isRequired,
  rows: PropTypes.array
};

Table.defaultProps = {
  rows: []
};

export default Table;
